// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { Container, Box } from '@mui/material';
import Navigation from './components/Navigation/Navigation';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import WebsitesPage from './pages/WebsitesPage';
import ReadyWebsitesPage from './pages/ReadyWebsitesPage';
import SearchPage from './pages/SearchPage';
import LogsPage from './pages/LogsPage';
import StatsPage from './pages/StatsPage';
import ContactsPage from './pages/ContactsPage';
import DoNotEmailList from './components/DoNotEmail/DoNotEmailList';
import WebsiteDetail from './components/Website/WebsiteDetail';
import Login from './components/Auth/Login';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import Register from './components/Auth/Register';
import Profile from './components/Profile/Profile';
import PrivateRoute from './components/Auth/PrivateRoute';
import CampaignList from './components/Campaign/CampaignList';
import CampaignForm from './components/Campaign/CampaignForm';
import GmailAccountList from './components/Gmail/GmailAccountList';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Contact from './pages/Contact';
import PublicLayout from './components/common/PublicLayout';
import ScrollToTop from './components/common/ScrollToTop';
import LiveOpenToasts from './components/Notifications/LiveOpenToasts';

// Helper to wrap component with PublicLayout
const withPublicLayout = (Component) => {
    return (props) => (
        <PublicLayout>
            <Component {...props} />
        </PublicLayout>
    );
};

// Update ROUTES configuration
const ROUTES = {
    public: [
        { 
            path: "/", 
            Component: withPublicLayout(Home),
            title: "Home",
            showInNav: false
        },
        { 
            path: "/auth/login", 
            Component: withPublicLayout(Login),
            title: "Login",
            showInNav: false
        },
        {
            path: "/auth/forgot-password",
            Component: withPublicLayout(ForgotPassword),
            title: "Forgot Password",
            showInNav: false
        },
        {
            path: "/auth/reset-password/:token",
            Component: withPublicLayout(ResetPassword),
            title: "Reset Password", 
            showInNav: false
        },
        { 
            path: "/auth/register", 
            Component: withPublicLayout(Register),
            title: "Register",
            showInNav: false
        },
        {
            path: "/terms",
            Component: withPublicLayout(Terms),
            title: "Terms of Service",
            showInNav: false
        },
        {
            path: "/privacy",
            Component: withPublicLayout(Privacy),
            title: "Privacy Policy",
            showInNav: false
        },
        {
            path: "/contact",
            Component: withPublicLayout(Contact),
            title: "Contact Us",
            showInNav: false
        }
    ],
    protected: [
        { 
            path: "/dashboard", 
            Component: Dashboard,
            title: "Dashboard",
            showInNav: true,
            order: 1 // For controlling navigation order
        },
        { 
            path: "/campaigns", 
            Component: CampaignList,
            title: "Campaigns",
            showInNav: true,
            order: 2
        },
        { 
            path: "/auth/profile", 
            Component: Profile,
            title: "Profile",
            showInNav: true,
            order: 3
        },
        { 
            path: "/gmail-accounts", 
            Component: GmailAccountList,
            title: "Gmail Accounts",
            showInNav: true,
            order: 4
        },
        { 
            path: "/contacts", 
            Component: ContactsPage,
            title: "Contacts",
            showInNav: true,
            order: 3  // Adjust order as needed
        },
        {
            path: "/contacts/do-not-email",
            Component: DoNotEmailList,
            title: "Do Not Email List",
            showInNav: false  // Hide from main nav
        },
        // Admin-only routes
        { 
            path: "/websites", 
            Component: WebsitesPage,
            title: "Websites",
            showInNav: true,
            adminOnly: true,
            order: 5
        },
        { 
            path: "/ready-websites", 
            Component: ReadyWebsitesPage,
            title: "Ready",
            showInNav: true,
            adminOnly: true,
            order: 6
        },
        { 
            path: "/search", 
            Component: SearchPage,
            title: "Search",
            showInNav: true,
            adminOnly: true,
            order: 7
        },
        { 
            path: "/logs", 
            Component: LogsPage,
            title: "Logs",
            showInNav: true,
            adminOnly: true,
            order: 8
        },
        { 
            path: "/stats", 
            Component: StatsPage,
            title: "Stats",
            showInNav: true,
            adminOnly: true,
            order: 9
        },
        // Routes that shouldn't appear in navigation
        { 
            path: "/websites/:id", 
            Component: WebsiteDetail,
            title: "Website Details",
            showInNav: false,
            adminOnly: true
        },
        { 
            path: "/campaigns/new", 
            Component: CampaignForm,
            title: "New Campaign",
            showInNav: false
        },
        { 
            path: "/campaigns/:id", 
            Component: CampaignForm,
            title: "Edit Campaign",
            showInNav: false,
            tabs: ['details', 'contacts']
        }
    ]
};

// Helper function to check if user is admin
const isAdmin = (user) => user && (user.id === 1 || user.id === 3);

const getPageTitle = (pathname, search) => {
    const siteName = window.location.hostname.includes('wpappdev.com') ? 
        'ArgusSTAGING' : 'ArgusCMS';
    
    // Check public routes
    const publicRoute = ROUTES.public.find(route => 
        route.path === pathname
    );
    if (publicRoute) return `${siteName} | ${publicRoute.title}`;

    // Check protected routes
    const protectedRoute = ROUTES.protected.find(route => {
        if (route.path.includes(':')) {
            const routePattern = new RegExp(
                '^' + route.path.replace(/:[\w-]+/g, '[\\w-]+') + '$'
            );
            return routePattern.test(pathname);
        }
        return route.path === pathname;
    });
    
    if (protectedRoute) {
        // Handle campaign tabs
        if (pathname.startsWith('/campaigns/') && protectedRoute.tabs) {
            const params = new URLSearchParams(search);
            const tab = parseInt(params.get('tab') || '0');
            const tabName = protectedRoute.tabs[tab];
            
            // Handle dynamic round tabs (tab >= 2)
            if (tabName) {
                return `${siteName} | Campaign ${tabName.charAt(0).toUpperCase() + tabName.slice(1)}`;
            } else {
                // Dynamic round tab
                return `${siteName} | Campaign Round ${tab - 1}`;
            }
        }
        return `${siteName} | ${protectedRoute.title}`;
    }
    
    return `${siteName} | Dashboard`;
};

const TitleUpdater = () => {
    const location = useLocation();
  
    useEffect(() => {
        document.title = getPageTitle(location.pathname, location.search);
    }, [location]);
  
    return null;
};

function App() {
    return (
        <Router>
            <div className="App">
                <ScrollToTop />
                <LiveOpenToasts />
                <TitleUpdater />
                <Navigation />
                {/* Remove padding and container for public routes */}
                <Box sx={{ 
                    flex: 1,
                    // Add top margin but remove bottom margin and padding
                    mt: 4
                }}>
                    <ErrorBoundary>
                        <Routes>
                            {/* Public Routes - No container */}
                            {ROUTES.public.map(({ path, Component }) => (
                                <Route 
                                    key={path} 
                                    path={path} 
                                    element={<Component />} 
                                />
                            ))}

                            {/* Protected Routes - Keep container */}
                            {ROUTES.protected.map(({ path, Component }) => (
                                <Route 
                                    key={path} 
                                    path={path} 
                                    element={
                                        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 }, pb: 4 }}>
                                            <PrivateRoute>
                                                <Component />
                                            </PrivateRoute>
                                        </Container>
                                    } 
                                />
                            ))}
                        </Routes>
                    </ErrorBoundary>
                </Box>
            </div>
        </Router>
    );
}

export default App;

// Export these for use in Navigation
export { ROUTES, isAdmin };